import { type UserRole } from '@shared/services';
import { PROFILE_PATH } from '@/app/paths';
import { authClient } from '@/shared/api/auth';
import { decodeAccessToken, resolveSafeReturnUrl, resourcePathByRole } from '@/shared/utils';

const isWithin = (path: string, base: string): boolean => path === base || path.startsWith(`${base}/`);

const isWithinAny = (path: string, bases: string[]): boolean => bases.some((base) => isWithin(path, base));

/**
 * Resolves the post-auth `returnUrl` with the same guarantees as {@link resolveSafeReturnUrl} —
 * origin-locked, no `/auth/*` — plus one exception: `/auth/profile`. Profile is the auth app's own
 * authorized page and is reachable by every signed-in role, so a guest bounced from Profile to login
 * must be returned there after sign-in (which `resolveSafeReturnUrl` refuses because it rejects all
 * `/auth/*`). The exception is scoped to this post-auth redirect only — Back navigation still goes
 * through `resolveSafeReturnUrl` and keeps rejecting every `/auth/*` target.
 */
const resolvePostAuthReturnUrl = (raw: string | null): string | null => {
  const safe = resolveSafeReturnUrl(raw);
  if (safe) {
    return safe;
  }

  if (!raw) {
    return null;
  }

  let url: URL;
  try {
    url = new URL(raw, window.location.origin);
  } catch {
    return null;
  }

  // Permit exactly the Profile path, origin-locked. Any query it carries (e.g. a panel `returnUrl`
  // threaded through login so Back still works) rides along in `href`; nothing else under `/auth` is
  // allowed through.
  if (url.origin === window.location.origin && url.pathname === PROFILE_PATH) {
    return url.href;
  }

  return null;
};

const redirect = (role: UserRole) => {
  const resources = resourcePathByRole(role) ?? [];

  // `returnUrl` rides in the URL query — set by the app that redirected here. Inline MFA verification
  // adopts the session on `/login` or `/switch`, whose URLs already carry `returnUrl`, so it survives
  // to this point without any mirroring.
  const safeReturnUrl = resolvePostAuthReturnUrl(new URLSearchParams(window.location.search).get('returnUrl'));

  // Honour `returnUrl` only if this (possibly just-switched) role can actually reach that app. An
  // account switch can change the role, and the originating `returnUrl` may point at an app the new
  // role cannot access — e.g. proven inspector (`/inspector`) → company root (`/admin`), or the
  // reverse. When the app is out of reach, fall through to the role's default resource instead of
  // following a stale path the new role would be bounced out of.
  if (safeReturnUrl) {
    const target = new URL(safeReturnUrl);

    // Profile is reachable by any authenticated role, so it bypasses the role-resource reachability
    // check that guards cross-app returnUrls after an account switch.
    const isProfile = target.pathname === PROFILE_PATH;

    if (target.href !== window.location.href && (isProfile || isWithinAny(target.pathname, resources))) {
      // `replace` so the spent `/auth/mfa` (or `/auth/switch`) entry does not linger in history where
      // Back would land the user on a dead ceremony.
      window.location.replace(safeReturnUrl);

      return;
    }
  }

  const [resource] = resources;

  // Assigning the path we are already on reloads the page, and the reload re-runs this
  // redirect: the access token is in memory only, but the refresh cookie is not, so the
  // session comes back every time and the loop never settles. Staying put instead leaves a
  // misrouted path visible rather than reloading forever.
  if (resource && !isWithin(window.location.pathname, resource)) {
    window.location.replace(resource);
  }
};

/**
 * Sends a signed-in user to their post-auth destination — a validated same-origin `returnUrl` from
 * the URL, otherwise the role's default resource. Exported so the flows that adopt a session while the
 * user is ALREADY authorized (account switching, and the switch-originated `/mfa` ceremony) can drive
 * the redirect explicitly: there is no guest→authorized transition in those flows, so `useAuthRedirect`
 * never fires. No-op when there is no access token.
 */
export const redirectToPostAuthDestination = () => {
  const accessToken = authClient.getAccessToken();
  if (!accessToken) {
    return;
  }

  const { role } = decodeAccessToken(accessToken);
  redirect(role);
};
